import React, { Component } from "react"
import styled from "styled-components"
import { inject, observer } from "mobx-react"

import Link from "next/link"
import GradientBackground from "~/components/traffic/lookup/Gradient"
import NYSBackgroundLogos from "~/components/traffic/lookup/landing/logos/NYSLogo"
import Spacer from "~/components/Spacer"
import SafeAndSecureLogo from "~/components/SafeAndSecureLogo"
import LookupDisclaimer from "~/components/LookupDisclaimer"
import ToggleSwitch from "~/components/Form/ToggleSwitch"
import SearchForm from "~/components/traffic/lookup/Forms"
import UserInfoInputs from "~/components/traffic/lookup/Forms/UserInfoInputs"
import NassauTabs from "~/components/traffic/lookup/Forms/NassauTabs"
import NYDMVForm from "~/components/traffic/lookup/NYDMVForm"
import DesktopContainer from "~/components/DesktopContainer"
import WhyUsContainer from "~/components/traffic/lookup/landing/WhyUsContainer"
import { DISCLAIMER_TYPES, FETCHER_NAMES_TITLES } from "~/utilities/constants"
import PHONE_NUMBERS from "~/components/altHeaders/phoneNumbers"
import WhyUseUs from "~/components/traffic-tickets/WhyUseUs"
import SupportPhoneNumber from "~/components/altHeaders/SupportPhoneNumber"
import ElfGoogleReviews from "~/components/_integrations/ElfGoogleReviews"
import withTranslations from "~/components/_hocs/withTranslations"
import TRACKING from "~/services/tracking"
import { trackLandingUnlessRedirecting } from "~/components/traffic/lookup/docket/designRedirect"

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SafeAndSecureContainer = styled.div`
  display: flex;
  direction: row;
  align-items: center;
`
const SafeAndSecureTextContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.primary};
`
const PayNYSTrafficText = styled.h1`
  margin: 0;
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${(props) => props.theme.colors.menuItem};
  font-size: ${(props) => (props.isMobile ? "1.8rem" : "2.1875rem")};
  font-weight: bold;
`
const SubText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: ${(props) => (props.isMobile ? "1rem" : "1.25rem")};
`

const DisclaimerText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: 0.95rem;
  text-align: justify;
`
const NYSDriversLicenseText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: #262626;
  font-size: 1rem;
`
const ToggleSwitchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  height: 54px;
`
const ToggleSwitchTextContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 1rem;
  padding: 0 2rem 0 0;
  max-width: 360px;
`
const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
`
const PhoneContainer = styled.div`
  height: 60px;
  display: flex;
  width: 270px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 5px 5px #cccccc;
  margin: 1% 2% 0px 0;
  ${(props) => props.theme.media.smallDesktop`
    display: none;
  `}
`
const PhoneSpace = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-end;
  ${(props) => props.theme.media.mobile` align-items: center;`}
`

const ParkingLink = styled.a`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
`

const StyledLink = styled.a`
  display: inline-block;
  color: rgba(48, 137, 243, 1);
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

@inject((all) => ({
  currentHost: all.store.ui.currentHost,
  fetcherName: all.store.trafficLookup.fetcherName,
  loadTrafficStates: all.store.trafficLookup.loadTrafficStates,
  isMobile: all.store.ui.isMobile,
  useNYDMVForm: all.store.trafficLookup.useNYDMVForm,
  toggleIsNYSLicense: all.store.trafficLookup.toggleIsNYSLicense,
  isNYSLicense: all.store.trafficLookup.isNYSLicense,
  isStateAffiliatedWithWinitLaw:
    all.store.trafficLookup.isStateAffiliatedWithWinitLaw,
}))
@observer
class LookupPage extends Component {
  async componentDidMount() {
    await this.props.loadTrafficStates()
    trackLandingUnlessRedirecting(() => TRACKING.trafficLookup.landing(this.props.fetcherName))
  }

  render() {
    const {
      translate,
      currentHost,
      isMobile,
      useNYDMVForm,
      isNYSLicense,
      toggleIsNYSLicense,
      isStateAffiliatedWithWinitLaw,
    } = this.props

    const phoneNumber =
      PHONE_NUMBERS.TRAFFIC_LOOKUP[this.props.fetcherName] ||
      PHONE_NUMBERS.TRAFFIC_LOOKUP.NY

    const titleType = "lookup"
    const title = translate(`traffic.newYork.title.${titleType}`)
    const subtitle = translate(`traffic.newYork.subtitle.${titleType}`)

    if (this.props.fetcherName === "NY" || this.props.fetcherName === "NY_STATE") {
      return (
        <PageContainer>
          <GradientBackground>
            <DesktopContainer>
              <PhoneSpace>
                <PhoneContainer>
                  <SupportPhoneNumber phoneNumber={phoneNumber} />
                </PhoneContainer>
              </PhoneSpace>
              <SafeAndSecureContainer>
                <SafeAndSecureLogo style={{ paddingRight: "8px" }} />
                <SafeAndSecureTextContainer>
                  {translate("traffic.secureIndicator")}
                </SafeAndSecureTextContainer>
              </SafeAndSecureContainer>
              <PayNYSTrafficText isMobile={isMobile}>{title}</PayNYSTrafficText>
              <SubText isMobile={isMobile}>{subtitle}</SubText>
              <SubText isMobile={isMobile}>
                {translate("traffic.newYork.parkingTicketAdvise")}
                <Link
                  href="/parking/pay?utm_source=Lookup&utm_medium=Click"
                  passHref
                >
                  <ParkingLink>
                    {translate("traffic.newYork.parkingTicketAdviseLink")}
                  </ParkingLink>
                </Link>
                .
              </SubText>
              <Spacer mobile={2} desktop={16} />
              {!useNYDMVForm && (
                <>
                  <NYSDriversLicenseText isMobile={isMobile}>
                    <ToggleSwitchContainer>
                      <ToggleSwitchTextContainer>
                        {translate("traffic.newYork.driverLicenseQuestion")}
                      </ToggleSwitchTextContainer>
                      <Spacer mobile={75} desktop={0} />
                      <ToggleSwitch
                        handleSwitch={toggleIsNYSLicense}
                        switchStatus={isNYSLicense}
                      />
                    </ToggleSwitchContainer>
                  </NYSDriversLicenseText>
                  <Spacer mobile={36} desktop={24} />
                </>
              )}
              {useNYDMVForm && <Spacer mobile={8} desktop={8} />}
              <FormContainer>
                {useNYDMVForm ? (
                  <NYDMVForm />
                ) : (
                  <>
                    {this.props.fetcherName === 'NY_NASSAU' && <NassauTabs />}
                    <UserInfoInputs />
                  </>
                )}
              </FormContainer>
              {!useNYDMVForm && (
                <>
                  <Spacer mobile={0} desktop={18} />
                  <FormContainer>
                    <SearchForm />
                  </FormContainer>
                </>
              )}
              <Spacer mobile={20} desktop={20} />
              <LookupDisclaimer
                type={
                  isStateAffiliatedWithWinitLaw
                    ? DISCLAIMER_TYPES.winitLaw
                    : DISCLAIMER_TYPES.winitLlc
                }
              />
            </DesktopContainer>
          </GradientBackground>
          <NYSBackgroundLogos>
            <DesktopContainer>
              <WhyUsContainer
                noUnderlay
                noModal
                currentHost={currentHost}
                isWILawAffiliated={isStateAffiliatedWithWinitLaw}
              />
            </DesktopContainer>
          </NYSBackgroundLogos>
        </PageContainer>
      )
    }
    return (
      <PageContainer>
        <GradientBackground>
          <DesktopContainer>
            <PhoneSpace>
              <PhoneContainer>
                <SupportPhoneNumber phoneNumber={phoneNumber} />
              </PhoneContainer>
            </PhoneSpace>
            <Spacer mobile={12} desktop={0} />
            <SafeAndSecureContainer>
              <SafeAndSecureLogo style={{ paddingRight: "8px" }} />
              <SafeAndSecureTextContainer>
                {translate("traffic.secureIndicator")}
              </SafeAndSecureTextContainer>
            </SafeAndSecureContainer>
            <Spacer mobile={12} desktop={10} />
            <PayNYSTrafficText isMobile={isMobile}>
              {(this.props.fetcherName === "PA" ||
                this.props.fetcherName === "PA_PHILADELPHIA")
                ? translate("traffic.otherStates.pa.title", {
                    state: FETCHER_NAMES_TITLES[this.props.fetcherName],
                  })
                : translate("traffic.otherStates.title", {
                    state: FETCHER_NAMES_TITLES[this.props.fetcherName],
                  })}
            </PayNYSTrafficText>
            <Spacer mobile={10} desktop={34} />
            <SubText isMobile={isMobile}>
              {(this.props.fetcherName === "PA" ||
                this.props.fetcherName === "PA_PHILADELPHIA")
                ? translate("traffic.otherStates.pa.subtitle")
                : translate("traffic.otherStates.subtitle")}
            </SubText>
            <Spacer mobile={8} desktop={24} />
            <FormContainer>
              {useNYDMVForm ? (
                <NYDMVForm />
              ) : (
                <>
                  {this.props.fetcherName === 'NY_NASSAU' && <NassauTabs />}
                  <UserInfoInputs />
                </>
              )}
            </FormContainer>
            {!useNYDMVForm && (
              <>
                <Spacer mobile={0} desktop={12} />
                <FormContainer>
                  <SearchForm />
                </FormContainer>
              </>
            )}
            <Spacer mobile={20} desktop={20} />
            <DisclaimerText isMobile={isMobile}>
              <>
                {translate("traffic.searchAgreement.text1")}
                <b>"{translate("form.general.submit.searchTickets")}"</b>
                {translate("traffic.searchAgreement.text2")}
                <StyledLink href="mailto:support@winitlaw.com">
                  support@winitlaw.com
                </StyledLink>
                {translate("traffic.searchAgreement.text3")}
                {translate("traffic.searchAgreement.text4")}
                <StyledLink href="mailto:support@winitlaw.com">
                  support@winitlaw.com
                </StyledLink>
                {translate("traffic.searchAgreement.text5")}
                <StyledLink
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.winitlaw.com/terms-of-service"
                      : "https://winitlaw.com/terms-of-service"
                  }
                >
                  {translate("general.termsOfService")}
                </StyledLink>
                {translate("traffic.searchAgreement.text6")}
                <StyledLink
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.winitlaw.com/privacy-policy"
                      : "https://winitlaw.com/privacy-policy"
                  }
                >
                  {translate("general.privacyPolicy")}
                </StyledLink>
                {translate("traffic.searchAgreement.text7")}
              </>
            </DisclaimerText>
            <Spacer mobile={20} desktop={20} />
            <DisclaimerText isMobile={isMobile}>
              <b>*</b>
              {isStateAffiliatedWithWinitLaw
                ? translate("winItLawDisclaimer")
                : translate("disclaimer")}
            </DisclaimerText>
            {isStateAffiliatedWithWinitLaw && (
              <>
                <Spacer mobile={20} desktop={20} />
                <DisclaimerText isMobile={isMobile}>
                  <b>**</b>
                  {translate("bwLawDisclaimer")}
                </DisclaimerText>
              </>
            )}
          </DesktopContainer>
        </GradientBackground>
        <Spacer mobile={20} desktop={20} />
        <WhyUsContainer
          noUnderlay
          noModal
          isWILawAffiliated={isStateAffiliatedWithWinitLaw}
        />
      </PageContainer>
    )
  }
}

export default withTranslations(LookupPage)
